import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  clearAccessToken,
  refreshSession,
  setAccessToken,
  setSessionLostHandler,
} from '@/api/client.js';
import { authApi } from '@/api/resources.js';
import { toApiError } from '@/api/client.js';
import { ROLES } from '@/lib/constants.js';

/**
 * Session state for the whole panel.
 *
 * ## The boot sequence, and why there is a splash
 *
 * The access token lives in memory, so a reload starts with none. What survives
 * is the httpOnly refresh cookie, and the only way to find out whether it is
 * still good is to spend it. So on mount the provider calls
 * `/admin/auth/refresh` exactly once and holds `status: 'restoring'` until it
 * settles.
 *
 * The splash is not cosmetic. Without it, `ProtectedRoute` would see "no token"
 * on the first render and bounce a signed-in operator to `/login` — where the
 * refresh would then land, and they would be redirected back. Three renders and
 * a visible flash to arrive where they already were.
 *
 * ## Why exactly once
 *
 * The refresh token rotates on use, and the API treats a replay of a rotated
 * token as a compromise: it revokes the whole session family. React 18's
 * StrictMode double-invokes effects in development, so a naive `useEffect`
 * would fire two refreshes on every dev reload — the second replaying the token
 * the first just rotated — and sign the operator out with a scary message. The
 * `bootRef` guard below is what stops that, and it is guarding a real hazard
 * rather than a lint rule.
 */

const AuthContext = createContext(null);

/** `restoring` → `authenticated` | `anonymous`. Nothing renders during the first. */
const STATUS = Object.freeze({
  RESTORING: 'restoring',
  AUTHENTICATED: 'authenticated',
  ANONYMOUS: 'anonymous',
});

export function AuthProvider({ children }) {
  const [status, setStatus] = useState(STATUS.RESTORING);
  const [admin, setAdmin] = useState(null);

  /**
   * Why the last session ended, when it ended by itself.
   *
   * Rendered as a notice on the login screen. "Your session expired" and "your
   * session was revoked for security reasons" are different events and an
   * operator should be told which — the second one may mean somebody else has
   * their cookie.
   */
  const [sessionNotice, setSessionNotice] = useState(null);

  const queryClient = useQueryClient();
  const bootRef = useRef(false);

  /** Wipes every trace of the session, client-side. */
  const reset = useCallback(() => {
    clearAccessToken();
    setAdmin(null);
    setStatus(STATUS.ANONYMOUS);
    // Otherwise the next operator to sign in on this tab sees the previous
    // one's cached tables until each query happens to refetch.
    queryClient.clear();
  }, [queryClient]);

  /* ---------------------------------------------------------------- */
  /* Boot                                                              */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;

    (async () => {
      try {
        const token = await refreshSession();
        if (!token) throw new Error('no session');

        const { admin: profile } = await authApi.me();
        setAdmin(profile);
        setStatus(STATUS.AUTHENTICATED);
      } catch {
        // No cookie, an expired one, or a revoked family. All three mean the
        // same thing here: show the login screen, quietly. A first visit is not
        // an error and must not be announced as one.
        clearAccessToken();
        setStatus(STATUS.ANONYMOUS);
      }
    })();
  }, []);

  /* ---------------------------------------------------------------- */
  /* Session lost mid-flight                                           */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    setSessionLostHandler(({ message, revoked }) => {
      setSessionNotice({ message, revoked });
      reset();
    });

    return () => setSessionLostHandler(null);
  }, [reset]);

  /* ---------------------------------------------------------------- */
  /* Actions                                                           */
  /* ---------------------------------------------------------------- */

  const login = useCallback(async (credentials) => {
    const { admin: profile, accessToken } = await authApi.login(credentials);
    setAccessToken(accessToken);
    setAdmin(profile);
    setSessionNotice(null);
    setStatus(STATUS.AUTHENTICATED);
    return profile;
  }, []);

  const logout = useCallback(
    async ({ allDevices = false } = {}) => {
      try {
        await authApi.logout(allDevices);
      } catch (error) {
        // A logout that could not reach the API still has to sign the operator
        // out locally — leaving them looking signed in because the network
        // blipped is the worse failure. The server-side token expires on its
        // own within 15 minutes regardless.
        console.warn(
          'Logout request failed; clearing the session locally.',
          toApiError(error).message,
        );
      } finally {
        reset();
      }
    },
    [reset],
  );

  /**
   * Called after a password change, which the API ends every session for —
   * including the one that made the request. There is nothing to log out of by
   * the time this runs; it exists to set the notice explaining why.
   */
  const endSessionAfterPasswordChange = useCallback(() => {
    setSessionNotice({
      message: 'Your password was changed. Please sign in again.',
      revoked: false,
    });
    reset();
  }, [reset]);

  const clearSessionNotice = useCallback(() => setSessionNotice(null), []);

  const value = useMemo(
    () => ({
      status,
      admin,
      sessionNotice,

      isRestoring: status === STATUS.RESTORING,
      isAuthenticated: status === STATUS.AUTHENTICATED,

      /**
       * Whether the operator may use the super-admin-only sections.
       *
       * Used to *hide* what they cannot use rather than to enforce anything —
       * the API is the enforcement, and it answers 403 regardless of what this
       * says. Hiding is a courtesy: letting somebody click into a page that
       * cannot load is a worse experience than not offering it.
       */
      isSuperAdmin: admin?.role === ROLES.SUPER_ADMIN,

      login,
      logout,
      endSessionAfterPasswordChange,
      clearSessionNotice,
    }),
    [
      status,
      admin,
      sessionNotice,
      login,
      logout,
      endSessionAfterPasswordChange,
      clearSessionNotice,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
};

export { STATUS as AUTH_STATUS };
